// src/components/DocumentHub/DocumentUpload.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Modal,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from "@mui/material";
import { IoMdAdd } from "react-icons/io";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchFolders, createFolder } from "../../api/documentApi";
import toast from "react-hot-toast";

function DocumentUpload({ open, onClose, onUpload, isSubmitting, vendorId, formData, setFormData}) {
  const queryClient = useQueryClient();
  const [addFolderOpen, setAddFolderOpen] = useState(false);
  const [newFolderData, setNewFolderData] = useState({ name: "", description: "" });

  const { data: folderData, isLoading: isLoadingFolders } = useQuery({
    queryKey: ["folders"],
    queryFn: fetchFolders,
    enabled: open,
  });

  const folders = Array.isArray(folderData?.data) ? folderData.data : (folderData?.data?.folders || []);

  const createMutation = useMutation({
    mutationFn: createFolder,
    onSuccess: (res) => {
      if (res.status) {
        toast.success("Folder created successfully!");
        queryClient.invalidateQueries(["folders"]);
        setAddFolderOpen(false);
        setNewFolderData({ name: "", description: "" });
      } else {
        toast.error(res.message || "Failed to create folder");
      }
    },
    onError: (err) => {
      const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || "An error occurred while creating the folder";
      toast.error(errMsg);
    },
  });

  const handleCreateFolder = () => {
    if (!newFolderData.name) {
      toast.error("Folder name is required");
      return;
    }
    createMutation.mutate(newFolderData);
  };

  useEffect(() => {
    setFormData((prev) => ({ ...prev, vendor_id: vendorId }));
  }, [vendorId]);

  const handleFileChange = (e) => {
    setFormData({ ...formData, files: Array.from(e.target.files) });
  };

  const handleSubmit = () => {
    onUpload(formData);
  };

  return (
    <>
    <Modal open={open} onClose={(_, reason) => reason !== "backdropClick" && onClose()}>
      <Box sx={modalStyle}>
        <Typography variant="h6" sx={{ fontSize: "22px", mb: 2 }}>
          Upload Document
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel>Document Type*</InputLabel>
          <Select
            value={formData.document_type || ""}
            onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
            label="Document Type"
          >
            <MenuItem value="Agreement">Agreement</MenuItem>
            <MenuItem value="SOF">SOF</MenuItem>
            <MenuItem value="Invoice">Invoice</MenuItem>
            <MenuItem value="Credit Note">Credit Note</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
        </FormControl>

        <Box display="flex" gap={1} alignItems="center" width="100%">
          <Autocomplete
            options={folders}
            getOptionLabel={(option) => option.name || ""}
            value={folders.find((f) => f.id === formData.folder_id) || null}
            onChange={(event, newValue) => {
              setFormData({ ...formData, folder_id: newValue ? newValue.id : "" });
            }}
            loading={isLoadingFolders}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Folder"
                placeholder="Search folders..."
                size="small"
              />
            )}
            sx={{ flexGrow: 1 }}
          />
          <IconButton
            sx={{ 
              minWidth: "40px", 
              height: "40px", 
              backgroundColor: "#f4e6fa", 
              color: "#7C2EA0",
              borderRadius: "8px",
              border: "1px solid #7C2EA0",
              "&:hover": { backgroundColor: "#7C2EA0", color: "white" } 
            }}
            onClick={() => setAddFolderOpen(true)}
            title="Create New Folder"
          >
            <IoMdAdd size={20} />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          size="small"
          label="Month Period (YYYY-MM)"
          type="month"
          value={formData.month_period || ""}
          onChange={(e) => setFormData({ ...formData, month_period: e.target.value })}
        />

        <TextField
          fullWidth
          size="small"
          label="Reference Number"
          value={formData.reference_number}
          onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
        />

        <TextField
          fullWidth
          size="small"
          label="Notes"
          multiline
          rows={2}
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />

        <Button
          variant="outlined"
          component="label"
          fullWidth
          size="small"
          sx={{ textTransform: "none", borderColor: "#7C2EA0", color: "#7C2EA0", py: 0.5 }}
        >
          {formData.files.length > 0 ? `${formData.files.length} file(s) selected` : "Choose Files"}
          <input type="file" multiple hidden onChange={handleFileChange} />
        </Button>

        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", gap: "10px" }}>
          <Button onClick={handleSubmit} sx={submitBtn}>
            {isSubmitting ? "Uploading..." : "Submit"}
          </Button>
          <Button onClick={onClose} sx={submitBtn}>
            Cancel
          </Button>
        </div>
      </Box>
    </Modal>

    <Dialog open={addFolderOpen} onClose={() => setAddFolderOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Folder</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            label="Folder Name"
            variant="outlined"
            size="small"
            value={newFolderData.name}
            onChange={(e) => setNewFolderData({ ...newFolderData, name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Description (Optional)"
            variant="outlined"
            size="small"
            multiline
            rows={3}
            value={newFolderData.description}
            onChange={(e) => setNewFolderData({ ...newFolderData, description: e.target.value })}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAddFolderOpen(false)}>Cancel</Button>
        <Button onClick={handleCreateFolder} variant="contained" disabled={createMutation.isLoading} sx={{ backgroundColor: "#4A148C" }}>
          {createMutation.isLoading ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  width: 420,
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 1.5,
};

const submitBtn = {
  flex: 1,
  fontSize: "14px",
  background: "#7C2EA0",
  color: "white",
  padding: "6px",
  textTransform: "none",
  "&:hover": { background: "#651b90" },
};

export default DocumentUpload;
